import axios from "axios"
import { getClientId } from "../services/Service"

const BASE_URL = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000').replace(/\/+$/, '');

// Helper function to get headers with authentication
const getHeaders = () => {
  const token = sessionStorage.getItem("token") || localStorage.getItem("token")
  return {
    "Content-Type": "application/json",
    Authorization: token ? `Bearer ${token}` : "",
    Accept: "application/json",
  }
}

// Helper function to handle API responses
// const handleResponse = async (response) => {
//   if (!response.ok) {
//     const errorData = await response.json().catch(() => ({}))
//     throw new Error(errorData.message || `HTTP error! status: ${response.status}`)
//   }
//   return await response.json()
// }

export const getRmsTicketDescriptionWithActivity = async (ticketId, clientId) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/rms/ticket-description-activity`,
      {
        ticketId: ticketId,
        clientId: clientId,
      },
      {
        headers: getHeaders(),
      },
    )
    return response.data
  } catch (error) {
    console.error("Error fetching RMS ticket description:", error.response?.data || error.message)
    return {
      status: false,
      message: error.response?.data?.message || "Failed to fetch RMS ticket description",
      data: [],
    }
  }
}

export const fetchQuestions = async (clientId) => {
  if (!clientId) throw new Error("Client ID is missing.")
  try {
    const response = await axios.get(`${BASE_URL}/get-questions`, {
      params: { client_id: clientId },
      headers: getHeaders()
    })
    return response.data || { questions: [] }
  } catch (error) {
    console.error("Error fetching questions:", error.response?.data || error.message)
    return { questions: [] }
  }
}

export const getInquiryStatusCount = async () => {
  const clientId = sessionStorage.getItem("clientId");
  try {
    const response = await axios.get(
      `${BASE_URL}/inquiry/status-count/${clientId}`,
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching inquiry status count:", error);
    throw error;
  }
};

export const addInquiryActivity = async ({
  inquiry_id,
  status,
  remark,
  followup_date,
  created_by
}) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/inquiry/activity`,
      {
        inquiry_id,
        status,
        remark,
        followup_date,
        created_by
      },
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    console.error("Error adding inquiry activity:", error);
    throw error;
  }
};

// export const updateInquiry = async ({
//   p_id,
//   p_idstatus,
//   p_idagent_remarks,
//   p_idNext_followup,
//   user_id,
// }) => {
//   try {
//     const response = await axios.put(`${BASE_URL}/update-inquiry`, {
//       p_id,
//       p_idstatus,
//       p_idagent_remarks,
//       p_idNext_followup,
//       user_id,
//     }, { headers: getHeaders() });

//     return response.data;
//   } catch (error) {
//     console.error("Error updating inquiry:", error);
//     throw error;
//   }
// };
export const updateInquiry = async ({
  p_id,
  p_idstatus,
  p_idagent_remarks,
  p_idNext_followup,
  user_id,
  isFinalStatus, // 👈 pass this from component
}) => {
  try {
    const payload = {
      p_id,
      p_idagent_remarks,
      user_id,
    };

    // 🔐 Only send status & follow-up if NOT final
    if (!isFinalStatus) {
      payload.p_idstatus = p_idstatus;
      payload.p_idNext_followup = p_idNext_followup;
    }

    const response = await axios.put(
      `${BASE_URL}/update-inquiry`,
      payload,
      { headers: getHeaders() }
    );

    return response.data;
  } catch (error) {
    if (error?.response?.status !== 401) {
      console.error("Error updating inquiry:", error);
    }
    throw error;
  }
};

export const assignInquiry = async (payload) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/inquiry/assign`,
      payload,
      { headers: getHeaders() }
    )

    return response.data
  } catch (error) {
    console.error("Error assigning inquiry:", error.response?.data || error.message)
    return {
      success: false,
      message: error.response?.data?.message || "Assignment failed",
    }
  }
}

export const assignInquiryToStaff = async ({
  inquiryId,
  staffId,
  createdBy,
  currentStatus,
  currentRemarks,
  nextFollowup,
}) => {
  return assignInquiry({
    inquiry_id: inquiryId,
    staff_id: staffId,
    created_by: createdBy,
    current_status: currentStatus,
    current_remarks: currentRemarks,
    next_followup: nextFollowup,
  })
}

export const getInquiryByClientId = async (clientId, page = 1, page_size = 10, other_query = "") => {
  try {
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('page_size', page_size);
    if (other_query) params.append('other_query', other_query);

    const response = await axios.get(`${BASE_URL}/inquiries/${clientId}?${params.toString()}`, {
      headers: getHeaders()
    });

    if (response && response.data && response.data.status) {
      return {
        ...response.data,
        total_records: response.data.total_records ?? (response.data.data?.length || 0),
      };
    } else {
      console.error("Invalid response data:", response);
      return { status: false, data: [], total_records: 0 };
    }
  } catch (error) {
    console.error("Error fetching inquiries:", error);
    return { status: false, data: [], total_records: 0 };
  }
};

export const getInquiries = async ({
  clientId,
  staff_id,
  status,
  page = 1,
  page_size = 1000,
  other_query = "",
} = {}) => {
  try {
    const params = new URLSearchParams()
    params.append("page", page)
    params.append("page_size", page_size)
    if (staff_id !== undefined && staff_id !== null && staff_id !== "") {
      params.append("staff_id", staff_id)
    }
    if (status && status !== "all") {
      params.append("status", status)
    }
    if (other_query) params.append("other_query", other_query)

    const response = await axios.get(`${BASE_URL}/inquiries/${clientId}?${params.toString()}`, {
      headers: getHeaders(),
    })

    if (response?.data) {
      return {
        ...response.data,
        total_records: response.data.total_records ?? (response.data.data?.length || 0),
      }
    }

    return { status: false, data: [], total_records: 0 }
  } catch (error) {
    console.error("Error fetching inquiries:", error.response?.data || error.message)
    return { status: false, data: [], total_records: 0 }
  }
}

export const getQuestions = async () => {
  const clientId = getClientId()
  if (!clientId) throw new Error("Client ID is missing.")
  try {
    const response = await axios.get(`${BASE_URL}/get-questions`, {
      params: { client_id: clientId },
      headers: getHeaders()
    })
    return response.data.questions
  } catch (error) {
    console.error("Error fetching questions:", error.response?.data || error.message)
    throw error
  }
}

export const sendQuestionsToAPI = async (questionData) => {
  const clientId = getClientId()
  if (!clientId) throw new Error("Client ID is missing.")
  try {
    const response = await axios.post(
      `${BASE_URL}/manage-question`,
      { ...questionData, client_id: clientId },
      { headers: getHeaders() },
    )
    return response.data
  } catch (error) {
    console.error("Error sending question:", error.response?.data || error.message)
    throw error
  }
}

export const deleteQuestion = async (questionData) => {
  const { p_id, p_question_text, p_client_id } = questionData
  if (!p_id) throw new Error("Question ID is missing.")
  if (!p_client_id) throw new Error("Client ID is missing.")
  if (!p_question_text) questionData.p_question_text = "No Text Available"
  try {
    console.log("📤 Sending DELETE request for ID:", p_id)
    const response = await axios.delete(`${BASE_URL}/delete-question`, {
      data: {
        action_type: "D",
        ...questionData,
      },
      headers: getHeaders(),
    })
    console.log("✅ Delete API Response:", response.data)
    return response.data
  } catch (error) {
    console.error("❌ Error deleting question:", error.response?.data || error.message)
    throw error
  }
}

export const updateQuestion = async (questionData) => {
  const clientId = getClientId()
  if (!clientId) throw new Error("Client ID is missing.")
  try {
    const validQuestions = questionData.questions.map((question) => ({
      ...question,
      p_id: question.p_id || question.questionId,
    }))
    console.log("Sending Update Request:", validQuestions)
    const response = await axios.put(
      `${BASE_URL}/update-question`,
      { questions: validQuestions, client_id: clientId },
      { headers: getHeaders() },
    )
    console.log("Update Response:", response.data)
    return response.data
  } catch (error) {
    console.error("Error updating question:", error.response?.data || error.message)
    throw error
  }
}

export const addStaff = async (staffData) => {
  try {
    const response = await axios.post(`${BASE_URL}/manage-user`, staffData, {
      headers: getHeaders(),
    })
    return response.data
  } catch (error) {
    console.error("❌ Error adding staff:", error.response?.data || error.message)
    throw error
  }
}

export const getStaff = async () => {
  const clientId = getClientId()
  if (!clientId) throw new Error("Client ID is missing.")
  try {
    const response = await axios.get(`${BASE_URL}/manage-user`, {
      params: {
        p_ClientId: clientId,
        p_role_abbreviation: "S",
      },
      headers: getHeaders()
    })
    console.log("✅ Fetched Staff for Client ID:", clientId, response.data)
    return response.data
  } catch (error) {
    console.error("❌ Error fetching staff:", error.response?.data || error.message)
    return { data: [] }
  }
}

export const getActiveStaff = (clientId) => {
  return axios.get(`${BASE_URL}/active-staff`, {
    params: {
      client_id: clientId,
    },
    headers: getHeaders(),
  })
}

export const softDeleteStaff = async (userId) => {
  try {
    const response = await axios.put(
      `${BASE_URL}/users/soft-delete`,
      {
        user_id: userId,
      },
      { headers: getHeaders() }
    );

    return response; // ✅ return FULL response

  } catch (error) {
    console.error(
      "Error in soft delete:",
      error.response?.data || error.message
    );

    throw error; // ✅ keep this
  }
};

export const updateStaff = async (staffData) => {
  try {
    const response = await axios.put(`${BASE_URL}/manage-user`, staffData, {
      headers: getHeaders(),
    })
    console.log("✅ Staff updated successfully:", response.data)
    return response.data
  } catch (error) {
    console.error("❌ Error updating staff:", error.response?.data || error.message)
    throw error
  }
}

export const updatePassword = async (userId, newPassword, confirmPassword) => {
  try {
    const response = await axios.post(`${BASE_URL}/public/update-password`, {
      user_id: userId,
      password: newPassword,
      password_confirmation: confirmPassword,
    }, { headers: getHeaders() })
    return response.data
  } catch (error) {
    console.error("❌ Error updating password:", error.response?.data || error.message)
    throw new Error(error.response?.data?.message || "Failed to update password.")
  }
}

export const fetchAllClients = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/clients`, { headers: getHeaders() })
    return response.data?.data || []
  } catch (error) {
    console.error("Error fetching clients:", error)
    return []
  }
}

export const getClientContactNumber = async (clientId) => {
  try {
    const clients = await fetchAllClients()
    const client = clients.find((c) => String(c.id) === String(clientId))
    return client?.ContactNumber || "8421924019"
  } catch (error) {
    console.error("Error getting client contact number:", error)
    return "8421924019"
  }
}

export const getAllStaffByClient = async (clientId) => {
  try {
    const res = await axios.get(`${BASE_URL}/username/by-client/${clientId}`, { headers: getHeaders() })
    return { status: true, data: res.data }
  } catch (error) {
    console.error("Failed to fetch staff list", error)
    return { status: false, data: [] }
  }
}

export const assignAgentToTicket = async (ticketActivityId, ticketId, assignedUserId, createdBy = 0) => {
  try {
    const response = await axios.put(`${BASE_URL}/update-ticket-activity`, {
      id: ticketActivityId,
      ticketId: ticketId,
      assignedTo: assignedUserId,
      status: null,
      createdBy: createdBy,
    }, { headers: getHeaders() })
    return response.data
  } catch (error) {
    console.error("Error assigning agent:", error)
    return { status: false, message: "Assignment failed." }
  }
}

export const getTicketsByClientId = async (clientId) => {
  if (!clientId) {
    throw new Error("Client ID is required.")
  }
  try {
    const response = await axios.get(`${BASE_URL}/tickets-with-details/${clientId}`, { headers: getHeaders() })
    const rawTickets = response?.data?.tickets
    if (Array.isArray(rawTickets)) {
      return {
        success: true,
        tickets: rawTickets,
      }
    } else {
      console.error("Unexpected response format:", response)
      return { success: false, tickets: [] }
    }
  } catch (error) {
    console.error("Error fetching tickets by client ID:", error)
    return {
      success: false,
      message: error.response?.data?.message || error.message,
      tickets: [],
    }
  }
}

export const getStaffList = async (clientId) => {
  try {
    const res = await axios.get(`${BASE_URL}/client/${clientId}/staff`, { headers: getHeaders() })
    return res.data || []
  } catch (err) {
    console.error("Error fetching staff:", err)
    return []
  }
}

export const assignAgentToTickets = async (ticketId, staffId, createdBy) => {
  try {
    const res = await axios.post(`${BASE_URL}/tickets/assign-agent`, {
      ticketIds: [ticketId],
      assignedTo: staffId,
      createdBy: createdBy,
    }, { headers: getHeaders() })
    return res.data
  } catch (err) {
    console.error("Assignment failed:", err)
    return null
  }
}

export const searchTicketActivity = async (assignedTo) => {
  try {
    const response = await axios.get(`${BASE_URL}/search-ticket-activity`, {
      params: { assignedTo },
      headers: getHeaders()
    })
    return response.data
  } catch (error) {
    console.error("Error searching ticket activity:", error)
    throw error
  }
}

export const uploadExcelFile = async (file, clientId, userId) => {
  if (!file) throw new Error("Excel file is required.")
  if (!clientId) throw new Error("Client ID is required.")
  if (!userId) throw new Error("User ID is required.")
  const formData = new FormData()
  formData.append("excel_file", file)
  formData.append("client_id", clientId)
  formData.append("user_id", userId)
  try {
    const response = await axios.post(`${BASE_URL}/upload/excel-files`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    return response.data
  } catch (error) {
    console.error("❌ Excel Upload Error:", error.response?.data || error.message)
    throw error
  }
}
export const markSubscriptionInactive = async (payload) => {

  const response = await fetch(
    `${process.env.REACT_APP_API_BASE_URL}/subscription/mark-inactive`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    }
  )

  const data = await response.json()

  if (!response.ok) {
    throw new Error(data.message || "Server error")
  }

  return data
}

export const markSubscriptionActive = async (payload) => {

  const response = await fetch(
    `${process.env.REACT_APP_API_BASE_URL}/subscription/mark-active`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    }
  )

  const data = await response.json()

  if (!response.ok) {
    throw new Error(data.message || "Server error")
  }

  return data
}

export const getInactiveAMCReport = async () => {

  const clientId = sessionStorage.getItem("clientId")

  const response = await axios.get(
    `${process.env.REACT_APP_API_BASE_URL}/report/amc-inactive`,
    {
      params: { client_id: clientId },
      headers: getHeaders()
    }
  )

  return response.data
}

export const getTicketCountsByClientId = async (clientId) => {
  if (!clientId) throw new Error("Client ID is required.")
  try {
    const response = await axios.get(`${BASE_URL}/ticket-counts-by-client`, {
      params: { clientId },
      headers: getHeaders()
    })
    return response.data || { status: false, data: {} }
  } catch (error) {
    console.error("Error fetching ticket counts by client ID:", error)
    return {
      status: false,
      message: error?.response?.data?.message || "API error",
      data: {},
    }
  }
}

export const getTodayNotificationCounts = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/notifications/today-counts`, { headers: getHeaders() })
    if (response?.data?.status) {
      return {
        success: true,
        visitorCount: response.data.visitor_count,
        adminCount: response.data.admin_count,
      }
    } else {
      console.error("Unexpected response format:", response)
      return {
        success: false,
        visitorCount: 0,
        adminCount: 0,
      }
    }
  } catch (error) {
    console.error("Error fetching today's notification counts:", error)
    return {
      success: false,
      visitorCount: 0,
      adminCount: 0,
    }
  }
}

export const getTodayComplaintNotifications = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/notifications/today-complaints`, { headers: getHeaders() })
    return response.data
  } catch (error) {
    console.error("Error fetching today's complaint notifications:", error)
    return { status: false, admin_notification: [], visitor_notification: [] }
  }
}

// AMC Report Services
export const getAMCReport = async () => {
  const clientId = getClientId()
  if (!clientId) throw new Error("Client ID is missing.")
  try {
    const response = await axios.get(`${BASE_URL}/report/amc`, {
      params: { client_id: clientId },
      headers: getHeaders(),
    })
    console.log("✅ AMC Report fetched successfully:", response.data)
    return response.data
  } catch (error) {
    console.error("❌ Error fetching AMC report:", error.response?.data || error.message)
    return {
      status: false,
      message: error.response?.data?.message || "Failed to fetch AMC report",
      data: [],
    }
  }
}

export const getExpiredAMCReport = async () => {
  const clientId = getClientId()
  if (!clientId) throw new Error("Client ID is missing.")
  try {
    const response = await axios.get(`${BASE_URL}/report/amc-expired`, {
      params: { client_id: clientId },
      headers: getHeaders(),
    })
    console.log("✅ Expired AMC Report fetched successfully:", response.data)
    return response.data
  } catch (error) {
    console.error("❌ Error fetching expired AMC report:", error.response?.data || error.message)
    return {
      status: false,
      message: error.response?.data?.message || "Failed to fetch expired AMC report",
      data: [],
    }
  }
}



export const getSubscriptionStats = async () => {
  const clientId = getClientId()
  if (!clientId) throw new Error("Client ID is missing.")
  try {
    const response = await axios.get(`${BASE_URL}/report/subscription-stats`, {
      params: { client_id: clientId },
      headers: getHeaders()
    })
    console.log("✅ Subscription stats fetched successfully:", response.data)
    return response.data
  } catch (error) {
    console.error("❌ Error fetching subscription stats:", error.response?.data || error.message)
    return {
      status: false,
      message: error.response?.data?.message || "Failed to fetch subscription stats",
      data: {},
    }
  }
}

// Update Records Services
export const searchRecords = async (searchCriteria) => {
  try {
    const response = await axios.post(`${BASE_URL}/search-records`, searchCriteria, {
      headers: getHeaders(),
    })
    return response.data
  } catch (error) {
    console.error("Search Records Error:", error.response?.data || error.message)
    throw error
  }
}

export const updateRecord = async (recordData) => {
  try {
    const response = await axios.post(`${BASE_URL}/update-record`, recordData, {
      headers: getHeaders(),
    })
    return response.data
  } catch (error) {
    console.error("Update Record Error:", error.response?.data || error.message)
    throw error
  }
}

export const bulkUpdateRecords = async (bulkUpdateData) => {
  try {
    const response = await axios.post(`${BASE_URL}/bulk-update-records`, bulkUpdateData, {
      headers: getHeaders(),
    })
    return response.data
  } catch (error) {
    console.error("Bulk Update Error:", error.response?.data || error.message)
    throw error
  }
}

export const getSubscriptionDetails = async (subscriptionId, clientId) => {
  try {
    const response = await axios.get(`${BASE_URL}/subscription-details/${subscriptionId}`, {
      params: { clientId },
      headers: getHeaders()
    })
    return response.data
  } catch (error) {
    console.error("Get Subscription Details Error:", error.response?.data || error.message)
    throw error
  }
}

export const getAreaCodeStats = async (recordIds, clientId) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/area-code-stats`,
      {
        recordIds,
        clientId,
      },
      {
        headers: getHeaders(),
      },
    )
    return response.data
  } catch (error) {
    console.error("Get Area Code Stats Error:", error.response?.data || error.message)
    throw error
  }
}

// Area Code Ticket Services
export const createAreaCodeWiseTickets = async (ticketData) => {
  try {
    const response = await axios.post(`${BASE_URL}/create-area-code-wise-tickets`, ticketData, {
      headers: getHeaders(),
    })
    return response.data
  } catch (error) {
    console.error("Create Area Code Wise Tickets Error:", error.response?.data || error.message)
    throw error
  }
}

export const getClientAreaCodes = async (clientId) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/get-client-area-codes`,
      {
        clientId: clientId,
      },
      {
        headers: getHeaders(),
      },
    )
    return response.data
  } catch (error) {
    console.error("Get Client Area Codes Error:", error.response?.data || error.message)
    throw error
  }
}

export const getUsersByAreaCode = async (clientId, areaCode) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/get-users-by-area-code`,
      {
        clientId: clientId,
        areaCode: areaCode,
      },
      {
        headers: getHeaders(),
      },
    )
    return response.data
  } catch (error) {
    console.error("Get Users By Area Code Error:", error.response?.data || error.message)
    throw error
  }
}

export const getAvailableAgents = async (clientId) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/get-available-agents`,
      {
        clientId: clientId,
      },
      {
        headers: getHeaders(),
      },
    )
    return response.data
  } catch (error) {
    console.error("Get Available Agents Error:", error.response?.data || error.message)
    throw error
  }
}

// User-based ticket creation services
export const searchUserByMobile = async (mobile, clientId) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/search-user-by-mobile`,
      {
        mobile: mobile,
        clientId: clientId,
      },
      {
        headers: getHeaders(),
      },
    )
    return response.data
  } catch (error) {
    console.error("Search User By Mobile Error:", error.response?.data || error.message)
    throw error
  }
}

export const createUserTicketsByMobile = async (ticketData) => {
  try {
    const response = await axios.post(`${BASE_URL}/create-user-tickets-by-mobile`, ticketData, {
      headers: getHeaders(),
    })
    return response.data
  } catch (error) {
    console.error("Create User Tickets By Mobile Error:", error.response?.data || error.message)
    throw error
  }
}

// ===== SCHEDULE RMS TICKET CREATION - UPDATED (NO PRODUCTID) =====
export const createScheduleRmsTicket = async (ticketData) => {
  try {
    // Create detailed description with device count and device list
    const deviceCount = ticketData.deviceIDs.length
    const deviceList = ticketData.deviceIDs.join(", ")
    const description = `RMS Schedule Request | Area: ${ticketData.areaCode} | Devices: ${deviceCount.toString().padStart(2, "0")} (${deviceList})`

    // Format payload to match PHP controller field names exactly
    const payload = {
      userId: ticketData.userId,
      device_ids: ticketData.deviceIDs, // PHP expects device_ids array
      areaCode: ticketData.areaCode,
      ClientId: ticketData.ClientId, // Capital C as expected by PHP
      complaintID: ticketData.complaintID,
      created_by: ticketData.createdBy,
      description: description,
      productID: ticketData.productID, // Capital ID as expected by PHP
      deviceCount: deviceCount,
      deviceList: deviceList,
    }

    console.log("Sending payload:", payload)

    const response = await fetch(`${BASE_URL}/schedule-rms-ticket`, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify(payload),
    })

    const result = await response.json()
    return result
  } catch (error) {
    console.error("Error creating schedule RMS ticket:", error)
    throw error
  }
}

export const parseRMSDescription = (description) => {
  if (!description || typeof description !== "string") {
    return {
      area: "",
      deviceCount: 0,
      deviceList: [],
      displayText: "RMS Schedule Request for my testing",
    }
  }

  // Extract area code
  const areaMatch = description.match(/Area:\s*([^|]+)/i)
  const area = areaMatch ? areaMatch[1].trim() : ""

  // Extract device count and list - handle both formats
  const deviceMatch = description.match(/Devices:\s*(\d+)\s*\(([^)]+)\)/i) || description.match(/Devices:\s*([^|]+)/i)

  let deviceCount = 0
  let deviceList = []

  if (deviceMatch) {
    if (deviceMatch[2]) {
      // Format: "Devices: 03 (AC105, AC106, AC107)"
      deviceCount = Number.parseInt(deviceMatch[1]) || 0
      deviceList = deviceMatch[2]
        .split(",")
        .map((d) => d.trim())
        .filter((d) => d)
    } else {
      // Format: "Devices: AC105, AC106, AC107"
      deviceList = deviceMatch[1]
        .split(",")
        .map((d) => d.trim())
        .filter((d) => d)
      deviceCount = deviceList.length
    }
  }

  return {
    area,
    deviceCount,
    deviceList,
    displayText: `Area: ${area} | Devices: ${deviceCount.toString().padStart(2, "0")} (${deviceList.join(", ")})`,
  }
}

export const getRecentInquiries = async (clientId) => {
  try {
    const response = await axios.get(`${BASE_URL}/client/${clientId}/recent-inquiries`, { headers: getHeaders() })
    if (response && response.data) {
      return {
        success: true,
        clientId: response.data.client_id,
        newInquiriesCount: response.data.new_inquiries_count,
        recentInquiries: response.data.recent_inquiries,
      }
    } else {
      console.error("Unexpected response format from recent-inquiries API:", response)
      return { success: false, newInquiriesCount: 0, recentInquiries: [] }
    }
  } catch (error) {
    console.error(`Error fetching recent inquiries for client ${clientId}:`, error)
    return { success: false, newInquiriesCount: 0, recentInquiries: [] }
  }
}

// Get ticket by ID
export const getTicketById = async (ticketId, clientId) => {
  try {
    const response = await axios.get(`${BASE_URL}/admin/ticket/${ticketId}`, {
      params: { clientId },
      headers: getHeaders()
    })
    return response.data
  } catch (error) {
    console.error("Get Ticket By ID Error:", error.response?.data || error.message)
    throw error
  }
}

// Search tickets
export const searchTickets = async (searchCriteria) => {
  try {
    const response = await axios.post(`${BASE_URL}/admin/search-tickets`, searchCriteria, {
      headers: getHeaders(),
    })
    return response.data
  } catch (error) {
    console.error("Search Tickets Error:", error.response?.data || error.message)
    throw error
  }
}

export const getTicketHistory = async (ticketId, clientId) => {
  try {
    const response = await axios.get(`${BASE_URL}/ticket-history`, {
      params: {
        ticketId,
        clientId,
      },
      headers: getHeaders()
    })
    return response.data
  } catch (error) {
    console.error("Error fetching ticket history:", error)
    return {
      status: "error",
      message: error.response?.data?.message || "Unknown error occurred",
      data: [],
    }
  }
}

// Visitor ticket creation functions (using fetch with getHeaders)
export const createNewVisitorAmcRequest = async (ticketData) => {
  try {
    const response = await fetch(`${BASE_URL}/create-new-visitor-amc-request`, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify({
        deviceID: ticketData.deviceID,
        complaintIDs: ticketData.complaintIDs,
        createdBy: ticketData.createdBy,
      }),
    })

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.message || "Failed to create visitor AMC request")
    }

    return {
      success: data.status,
      message: data.message,
      ticketId: data.ticket_id,
    }
  } catch (error) {
    console.error("Error creating visitor AMC request:", error)
    throw error
  }
}

export const createNewVisitorCallBasedService = async (ticketData) => {
  try {
    const response = await fetch(`${BASE_URL}/create-new-visitor-call-based-service`, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify({
        deviceID: ticketData.deviceID,
        complaintIDs: ticketData.complaintIDs,
        createdBy: ticketData.createdBy,
      }),
    })

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.message || "Failed to create visitor call-based service")
    }

    return {
      success: data.status,
      message: data.message,
      ticketId: data.ticket_id,
    }
  } catch (error) {
    console.error("Error creating visitor call-based service:", error)
    throw error
  }
}

export const createExpiredAmcTicket = async (ticketData) => {
  try {
    const response = await fetch(`${BASE_URL}/create-expired-amc-ticket`, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify({
        deviceID: ticketData.deviceID,
        complaintIDs: ticketData.complaintIDs,
        createdBy: ticketData.createdBy,
      }),
    })

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.message || "Failed to create expired AMC ticket")
    }

    return {
      success: data.status,
      message: data.message,
      ticketId: data.ticket_id,
    }
  } catch (error) {
    console.error("Error creating expired AMC ticket:", error)
    throw error
  }
}

export const createAmcCallServiceTicket = async (ticketData) => {
  try {
    const response = await fetch(`${BASE_URL}/create-amc-call-service-ticket`, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify({
        deviceID: ticketData.deviceID,
        complaintIDs: ticketData.complaintIDs,
        createdBy: ticketData.createdBy,
      }),
    })

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.message || "Failed to create AMC call service ticket")
    }

    return {
      success: data.status,
      message: data.message,
      ticketId: data.ticket_id,
    }
  } catch (error) {
    console.error("Error creating AMC call service ticket:", error)
    throw error
  }
}

export const createVisitorTicket = async (ticketData) => {
  try {
    const response = await fetch(`${BASE_URL}/create-visitor-ticket`, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify({
        deviceID: ticketData.deviceID,
        complaintIDs: ticketData.complaintIDs,
        createdBy: ticketData.createdBy,
      }),
    })

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.message || "Failed to create visitor ticket")
    }

    return {
      success: data.status,
      message: data.message,
      ticketId: data.ticket_id,
    }
  } catch (error) {
    console.error("Error creating visitor ticket:", error)
    throw error
  }
}

// ===== ADMIN SERVICE CLASS FOR BACKWARD COMPATIBILITY =====
class AdminService {
  constructor() {
    this.baseURL = BASE_URL
  }

  // Helper method to get headers with authentication
  getHeaders() {
    const token = sessionStorage.getItem("token")
    return {
      "Content-Type": "application/json",
      Authorization: token ? `Bearer ${token}` : "",
      Accept: "application/json",
    }
  }

  // Helper method to handle API responses
  async handleResponse(response) {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`)
    }
    return await response.json()
  }

  async getRmsTicketDescriptionWithActivity(ticketId, clientId) {
    return await getRmsTicketDescriptionWithActivity(ticketId, clientId)
  }

  async searchRecords(searchCriteria) {
    return await searchRecords(searchCriteria)
  }

  async updateRecord(recordData) {
    return await updateRecord(recordData)
  }

  async bulkUpdateRecords(bulkUpdateData) {
    return await bulkUpdateRecords(bulkUpdateData)
  }

  async getSubscriptionDetails(subscriptionId, clientId) {
    return await getSubscriptionDetails(subscriptionId, clientId)
  }

  async getAreaCodeStats(recordIds, clientId) {
    return await getAreaCodeStats(recordIds, clientId)
  }

  async uploadExcel(formData) {
    const file = formData.get("excel_file")
    const clientId = formData.get("client_id")
    const userId = formData.get("user_id")
    return await uploadExcelFile(file, clientId, userId)
  }

  async getAMCReport(filters = {}) {
    return await getAMCReport()
  }

  async getExpiredAMCReport(filters = {}) {
    return await getExpiredAMCReport()
  }

  async getSubscriptionStats() {
    return await getSubscriptionStats()
  }

  async getTicketsWithDetails(clientId) {
    return await getTicketsByClientId(clientId)
  }

  async updateTicketActivity(ticketData) {
    return await assignAgentToTicket(ticketData.id, ticketData.ticketId, ticketData.assignedTo, ticketData.createdBy)
  }

  async assignAgentToTickets(assignmentData) {
    return await assignAgentToTickets(assignmentData.ticketIds[0], assignmentData.assignedTo, assignmentData.createdBy)
  }

  async getStaffByClient(clientId) {
    return await getStaffList(clientId)
  }
  async updateTicketStatus(ticketData) {
    try {
      const response = await axios.put(
        `${this.baseURL}/update-ticket-activity`,
        ticketData,
        { headers: this.getHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error("Error updating ticket status:", error.response?.data || error.message);
      throw error;
    }
  }
  async manageUser(userData, action = "POST") {
    if (action === "POST") {
      return await addStaff(userData)
    } else if (action === "PUT") {
      return await updateStaff(userData)
    }
    throw new Error("Unsupported action")
  }

  async getUsers(filters = {}) {
    return await getStaff()
  }

  async softDeleteUser(userId, clientId) {
    return await softDeleteStaff(userId)
  }

  async createAreaCodeWiseTickets(ticketData) {
    return await createAreaCodeWiseTickets(ticketData)
  }

  async getClientAreaCodes(clientId) {
    return await getClientAreaCodes(clientId)
  }

  async getUsersByAreaCode(clientId, areaCode) {
    return await getUsersByAreaCode(clientId, areaCode)
  }

  async getAvailableAgents(clientId) {
    return await getAvailableAgents(clientId)
  }

  async searchUserByMobile(mobile, clientId) {
    return await searchUserByMobile(mobile, clientId)
  }

  async createUserTicketsByMobile(ticketData) {
    return await createUserTicketsByMobile(ticketData)
  }

  async createScheduleRmsTicket(ticketData) {
    return await createScheduleRmsTicket(ticketData)
  }

  async getTicketById(ticketId, clientId) {
    return await getTicketById(ticketId, clientId)
  }

  async searchTickets(searchCriteria) {
    return await searchTickets(searchCriteria)
  }
}

// Export singleton instance for backward compatibility
const adminService = new AdminService()
export default adminService
